/**
 * ClientSiteView.jsx — Rê Leve IA (Etapa 6)
 * Experiência pública do cliente.
 * REGRA: nunca exibir dados técnicos, financeiros internos, segmento RFM,
 * guardrails, logs, ticket médio ou dados de outro cliente.
 */

import { useState, useEffect, useRef } from 'react';
import { getAllKits, getAllPromocoes } from '../lib/dataService';
import { generateBotResponse } from '../lib/chatbotEngine';
import { hasCalorieConsent } from '../lib/consentEngine';
import { getSimulatedCheckoutState, clearSimulatedCheckout } from '../lib/orderSimulationEngine';
import { getCrossSellSuggestion } from '../lib/commercialJourneyEngine';
import CalorieTrackerPanel from './CalorieTrackerPanel';
import { PaymentPanelClient } from './PaymentPanel';
import FAQChatbotPanel from './FAQChatbotPanel';
import MobileBottomNav from './MobileBottomNav';
import appConfig from '../config/appConfig';
import { sendChatToApi } from '../lib/apiClient';
import { recordChatEvent, recordOrderClosed, recordUpsellAdded, recordCrossSellAdded } from '../lib/chatEventsLogger';
import { getAssistantName } from '../lib/assistantConfig';
import { recordHumanRequest, recordSalesOpportunity, addPendingQuestion } from '../lib/pendingQuestionsEngine';

// ── Botões rápidos ─────────────────────────────────────────────────────────────

const QUICK_BUTTONS = [
  { id: 'semana',    label: '📅 Montar minha semana',       message: 'Quero montar minha semana' },
  { id: 'kit_dia',  label: '🥗 Quero um kit para hoje',     message: 'Quero um kit para hoje' },
  { id: 'calorico', label: '📊 Acompanhamento calórico',    message: 'Quero registrar minhas calorias' },
  { id: 'promocoes',label: '🎁 Ver promoções',              message: 'Quais são as promoções?' },
  { id: 'indicar',  label: '🤝 Indicar um amigo',           message: 'Quero indicar um amigo' },
  { id: 'whatsapp', label: '💬 Falar no WhatsApp',          message: 'Quero falar no WhatsApp' },
];

// ── Hero ───────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <div className="cs-hero">
      <div className="cs-hero-inner">
        <div className="cs-hero-badge">🌱 Rê Leve · Recife · vida mais leve</div>
        <h1 className="cs-hero-title">A semana mais leve<br/>começa no almoço.</h1>
        <p className="cs-hero-subtitle">
          Marmita pronta, cardápio que muda toda semana, montada do teu jeito. Sem peso na mochila, sem peso na cabeça — só comida boa de quem cuida.
        </p>
        <a {...appConfig.whatsAppAnchorProps('Oi! Vim aqui pra deixar a semana mais leve. Bora montar minha marmita? 🌿')} className="cs-btn cs-btn--hero rl-pulse">🥗 Quero leveza na minha rotina →</a>
        <div style={{ marginTop: '12px', fontSize: '12px', color: 'rgba(255,255,255,0.78)', letterSpacing: '0.04em' }}>
          ✓ +5 mil marmitas entregues em 2026 · ★ 4.9 no Google · 🚚 chega antes do almoço
        </div>
      </div>
    </div>
  );
}

// ── Vídeo da marca ─────────────────────────────────────────────────────────────

function VideoBrand() {
  const [played, setPlayed] = useState(false);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  function handlePlay() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true; // autoplay precisa de muted
    const playPromise = v.play();
    if (playPromise !== undefined) {
      playPromise.then(() => setPlayed(true)).catch(() => setError(true));
    }
  }

  return (
    <section className="cs-section" aria-label="Vídeo da Rê Leve">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: '1.5rem',
        marginBottom: '1.5rem',
        textAlign: 'center',
      }}>
        <div>
          <h2 className="cs-section-title" style={{ display: 'inline-block', paddingLeft: '1rem' }}>
            Veja a Rê Leve em ação
          </h2>
          <p style={{ fontSize: '0.95rem', color: 'var(--rl-ink-2)', marginTop: '0.5rem', maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>
            Comida real, feita por gente, entregue na sua mesa em Recife.
          </p>
        </div>
      </div>

      <div style={{
        position: 'relative',
        aspectRatio: '848 / 478',
        maxWidth: '900px',
        margin: '0 auto',
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(20,22,15,0.20)',
        background: '#1B1F1A',
      }}>
        <video
          ref={videoRef}
          src="/releve-promo.mp4"
          poster="/releve-promo-poster.jpg"
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          onError={() => setError(true)}
          onPlay={() => setPlayed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {!played && !error && (
          <button
            type="button"
            onClick={handlePlay}
            aria-label="Tocar vídeo"
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(20,22,15,0.35)',
              border: 'none', cursor: 'pointer',
            }}
          >
            <span style={{
              width: '88px', height: '88px',
              borderRadius: '50%',
              background: 'var(--rl-mostard)',
              color: 'var(--rl-ink)',
              fontSize: '34px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
            }}>▶</span>
          </button>
        )}

        {error && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: '#fff', padding: '2rem', textAlign: 'center',
            background: 'rgba(20,22,15,0.75)',
          }}>
            <p style={{ fontFamily: 'var(--rl-font-display)', fontSize: '1.2rem', marginBottom: '0.5rem' }}>
              Vídeo indisponível
            </p>
            <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>
              Confere tua conexão e atualiza a página.
            </p>
          </div>
        )}

        <div style={{
          position: 'absolute',
          bottom: '14px', left: '14px',
          background: 'rgba(20,22,15,0.7)',
          color: '#fff',
          padding: '6px 12px',
          borderRadius: '999px',
          fontFamily: 'var(--rl-font-caps)',
          fontSize: '11px',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          pointerEvents: 'none',
        }}>
          🌱 Rê Leve · vídeo da marca
        </div>
      </div>
    </section>
  );
}

// ── Card de checkout simulado ──────────────────────────────────────────────────

function CheckoutCard({ checkout, calorieConsent, onClear, onActivateCalorie }) {
  if (!checkout) return null;

  return (
    <div className="cs-checkout-card">
      <div className="cs-checkout-header">
        <span className="cs-checkout-icon">🛒</span>
        <div>
          <p className="cs-checkout-title">Resumo para WhatsApp</p>
          <span className="cs-badge cs-badge--confirmed">✅ Kit confirmado</span>
        </div>
      </div>

      <p className="cs-checkout-kit">{checkout.kitName}</p>

      <div className="cs-checkout-summary">
        <p className="cs-checkout-summary-text">
          Olá! Quero montar o kit <strong>{checkout.kitName}</strong>. Tenho interesse em finalizar pelo WhatsApp.
        </p>
      </div>

      {calorieConsent ? (
        <div className="cs-checkout-badge-row">
          <span className="cs-badge cs-badge--calorie">📊 Plano calórico informativo criado</span>
        </div>
      ) : (
        <div className="cs-checkout-no-consent">
          <p className="cs-checkout-no-consent-text">
            Você pode ativar o acompanhamento calórico informativo antes de registrar consumo.
          </p>
          <button className="cs-btn cs-btn--secondary" onClick={onActivateCalorie}>
            📊 Ativar acompanhamento calórico
          </button>
        </div>
      )}

      <div className="cs-checkout-actions">
        <a
          {...appConfig.whatsAppAnchorProps(
            'Oi! Quero finalizar meu pedido: ' + (checkout?.kitName || 'kit Rê Leve') + '. 🌱'
          )}
          className="cs-btn cs-btn--whatsapp"
        >
          💬 Finalizar pelo WhatsApp
        </a>
        <button className="cs-btn cs-btn--ghost" onClick={onClear}>
          Limpar
        </button>
      </div>

      <p className="cs-checkout-disclaimer">
        Resumo simulado para protótipo. WhatsApp real será integrado na próxima etapa.
      </p>
    </div>
  );
}

// ── Chatbot ────────────────────────────────────────────────────────────────────

function ClientChat({ simCustomer }) {
  const clienteId = simCustomer?.clienteId || simCustomer?.id || null;

  const [messages, setMessages] = useState(() => [
    { id: 1, sender: 'bot', text: `Oi! Sou a ${getAssistantName()}, assistente da Rê Leve 🌱 Bora deixar tua semana mais leve? Te ajudo em 3 perguntas.` }
  ]);
  const [input, setInput] = useState('');
  const [pendingConsent, setPendingConsent] = useState(false);
  const [calorieActive, setCalorieActive] = useState(() =>
    clienteId ? hasCalorieConsent(clienteId) : false
  );
  const [lastRecommendation, setLastRecommendation] = useState(null);
  const [checkoutState, setCheckoutState] = useState(() =>
    clienteId ? getSimulatedCheckoutState(clienteId) : null
  );
  const [activePayment, setActivePayment] = useState(null);

  useEffect(() => {
    setCalorieActive(clienteId ? hasCalorieConsent(clienteId) : false);
    setCheckoutState(clienteId ? getSimulatedCheckoutState(clienteId) : null);
  }, [clienteId]);

  function addMessages(userText, botResponse) {
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    const shouldShowConsent = botResponse.shouldAskConsent && !calorieActive;
    const custName = simCustomer?.nome || simCustomer?.name || `Cliente ${clienteId || 'visitante'}`;
    const custSeg  = simCustomer?.segmentoComercial || simCustomer?.segmento || 'visitante';

    // ── Captura para painel de Conversas (em tempo real) ──────────────────
    recordChatEvent({
      role: 'user',
      text: userText,
      clienteId: clienteId || 'visitante',
      useCase: 'sales_relationship',
    });

    // ── Detecção automática de intervenção humana ────────────────────────
    const lower = (userText || '').toLowerCase();

    // 1. Cliente pediu falar com humano
    const wantsHuman = /\b(falar com (humano|alguém|alguem|pessoa|atendente|gente)|com uma pessoa|atendimento humano|me ajuda alguém|preciso de alguém|cad[eê] (alguém|gente))\b/i.test(userText)
                       || botResponse.shouldEscalateToHuman;
    if (wantsHuman) {
      recordHumanRequest({
        clienteId: clienteId || 'visitante',
        customerName: custName,
        customerSegment: custSeg,
        context: 'Cliente solicitou falar com humano durante conversa com a assistente.',
        leveResponse: botResponse.text,
      });
    }

    // 2. Oportunidade de venda alta (compra corporativa, volume grande, alto-ticket)
    const corporateMatch = /\b(empresa|corporativ|funcion[áa]rios?|equipe|escrit[óo]rio|coletivo|cnpj)\b/i.test(userText);
    const volumeMatch    = /\b(\d{2,3})\s*(marmitas?|kits?|refei[çc][õo]es)\b/i.exec(userText);
    if (corporateMatch && volumeMatch) {
      const vol = parseInt(volumeMatch[1], 10);
      if (vol >= 10) {
        recordSalesOpportunity({
          clienteId: clienteId || 'visitante',
          customerName: custName,
          customerSegment: custSeg,
          signal: userText,
          estimatedValue: vol * 22 * 4, // R$22 médio × 4 semanas
          context: 'Cliente mencionou volume corporativo. Detectado: ' + vol + ' marmitas.',
        });
      }
    }
    // Cliente fiel/alto-valor expressando interesse em assinatura ou semana
    else if ((custSeg.includes('fiel') || custSeg.includes('alto')) &&
             /\b(semana inteira|toda semana|assinatura|m[êe]s|mensal|fechar tudo)\b/i.test(userText)) {
      recordSalesOpportunity({
        clienteId: clienteId || 'visitante',
        customerName: custName,
        customerSegment: custSeg,
        signal: userText,
        estimatedValue: 600,
        context: 'Cliente recorrente mencionando recorrência longa — momento de fechar assinatura.',
      });
    }
    recordChatEvent({
      role: 'bot',
      text: botResponse.text,
      clienteId: clienteId || 'visitante',
      intent: botResponse.intent,
      source: botResponse.source || 'local',
      activeGuardrailIds: botResponse.activeGuardrailIds || [],
      useCase: 'sales_relationship',
    });

    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: userText, timestamp: now },
      {
        id: Date.now() + 1,
        sender: 'bot',
        text: botResponse.text,
        timestamp: now,
        shouldEscalate: botResponse.shouldEscalateToHuman,
        shouldAskConsent: shouldShowConsent,
        cta: botResponse.cta,
        recommendation: botResponse.recommendation,
        confirmedKit: botResponse.confirmedKit,
        whatsappSummary: botResponse.whatsappSummary,
        caloriePlanStatus: botResponse.caloriePlanStatus,
        mealRegistered: botResponse.mealRegistered,
        intent: botResponse.intent,
        source: botResponse.source,
      },
    ]);

    if (shouldShowConsent) setPendingConsent(true);
    if (botResponse.calorieTrackingActive) setCalorieActive(true);
    if (botResponse.consentDenied) setPendingConsent(false);
    if (botResponse.recommendation) setLastRecommendation(botResponse.recommendation);

    // Atualizar checkout após confirmação
    if (botResponse.confirmedKit && clienteId) {
      setCheckoutState(getSimulatedCheckoutState(clienteId));
      setCalorieActive(hasCalorieConsent(clienteId));
    }
    if (botResponse.caloriePlanStatus?.success) {
      setCalorieActive(true);
    }
  }

  function handleQuick(btn) {
    const response = generateBotResponse({
      message: btn.message,
      customer: simCustomer,
      selectedKit: lastRecommendation,
      chatHistory: messages,
      pendingConsent,
    });
    addMessages(btn.label, response);
  }

  async function handleSend() {
    if (!input.trim()) return;
    const userText = input.trim();
    setInput('');

    // Motor local para dados estruturais (kit, consentimento, checkout)
    const localResponse = generateBotResponse({
      message: userText,
      customer: simCustomer,
      selectedKit: lastRecommendation,
      chatHistory: messages,
      pendingConsent,
    });

    // Tenta API/LLM para resposta de texto mais fluida
    let finalText = localResponse.text;
    let source = 'local';

    if (appConfig.enableRealApi) {
      try {
        const api = await sendChatToApi({
          clienteId,
          message: userText,
          useCase: 'sales_relationship',
          source: 'client_site',
        });
        if (api?.text) {
          finalText = api.text;
          source = api.llmUsed ? 'llm' : (api.fallbackUsed ? 'fallback' : 'deterministic');
        }
      } catch {}
    }

    addMessages(userText, { ...localResponse, text: finalText, source });
  }

  function handleConsentYes() {
    setPendingConsent(false);
    const response = generateBotResponse({
      message: 'sim autorizo',
      customer: simCustomer,
      selectedKit: lastRecommendation,
      chatHistory: messages,
      pendingConsent: true,
    });
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: 'Sim, autorizo', timestamp: now },
      { id: Date.now() + 1, sender: 'bot',  text: response.text,  timestamp: now, caloriePlanStatus: response.caloriePlanStatus },
    ]);
    setCalorieActive(true);
    if (clienteId) setCheckoutState(getSimulatedCheckoutState(clienteId));
  }

  function handleConsentNo() {
    setPendingConsent(false);
    const response = generateBotResponse({
      message: 'agora não',
      customer: simCustomer,
      chatHistory: messages,
      pendingConsent: true,
    });
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: 'Agora não', timestamp: now },
      { id: Date.now() + 1, sender: 'bot',  text: response.text, timestamp: now },
    ]);
  }

  function handleConfirmKit(rec) {
    const response = generateBotResponse({
      message: 'confirmar kit',
      customer: simCustomer,
      selectedKit: rec || lastRecommendation,
      chatHistory: messages,
      pendingConsent,
    });
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: 'Confirmar este kit', timestamp: now },
      { id: Date.now() + 1, sender: 'bot',  text: response.text, timestamp: now, confirmedKit: response.confirmedKit, whatsappSummary: response.whatsappSummary, caloriePlanStatus: response.caloriePlanStatus, cta: response.cta },
    ]);
    if (response.confirmedKit && clienteId) {
      setCheckoutState(getSimulatedCheckoutState(clienteId));

      // ── Notifica o operador em tempo real (kit fechado) ──────────────
      const kit = response.confirmedKit;
      const kitPrice = kit.preco || kit.price || 24.90;
      recordOrderClosed({
        clienteId,
        customerName: simCustomer?.nome || simCustomer?.name || `Cliente ${clienteId}`,
        kitId: kit.id,
        kitName: kit.nome || kit.name,
        kitPrice,
        upsells: [],
        crossSells: [],
        total: kitPrice,
      });
    }
    if (response.calorieTrackingActive || response.caloriePlanStatus?.success) {
      setCalorieActive(true);
    }
  }

  function handleActivateCalorie() {
    setPendingConsent(true);
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(), sender: 'bot', text: 'Para ativar o acompanhamento calórico informativo, preciso da sua autorização. Posso seguir?', timestamp: now, shouldAskConsent: true },
    ]);
  }

  function handleClearCheckout() {
    if (clienteId) clearSimulatedCheckout(clienteId);
    setCheckoutState(null);
    setActivePayment(null);
  }

  function handlePaymentApproved({ payment, kitName }) {
    setActivePayment(payment);
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      {
        id: Date.now(),
        sender: 'bot',
        text: `✅ Pagamento confirmado no protótipo! O próximo passo seria finalizar separação, entrega ou atendimento pelo WhatsApp.`,
        timestamp: now,
      },
    ]);
  }

  function handleRegisterMeal() {
    const response = generateBotResponse({
      message: 'registrei o almoço',
      customer: simCustomer,
      selectedKit: lastRecommendation,
      chatHistory: messages,
      pendingConsent,
    });
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: 'Registrar almoço', timestamp: now },
      { id: Date.now() + 1, sender: 'bot',  text: response.text, timestamp: now },
    ]);
  }

  function handleCrossSell() {
    const response = generateBotResponse({
      message: 'quero um suco',
      customer: simCustomer,
      selectedKit: lastRecommendation,
      chatHistory: messages,
      pendingConsent,
    });
    const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [
      ...prev,
      { id: Date.now(),     sender: 'user', text: 'Quero suco ou snack', timestamp: now },
      { id: Date.now() + 1, sender: 'bot',  text: response.text, timestamp: now },
    ]);

    // ── Notifica o operador (cliente abriu cross-sell) ──────────────────
    recordCrossSellAdded({
      clienteId: clienteId || 'visitante',
      customerName: simCustomer?.nome || simCustomer?.name || `Cliente ${clienteId || 'visitante'}`,
      suggestedItem: 'Suco verde ou snack',
      price: 6,
    });
  }

  return (
    <div className="cs-chat">
      <div className="cs-chat-header">
        <span className="cs-chat-avatar rl-leve-avatar">🌱</span>
        <div>
          <p className="cs-chat-name">{getAssistantName()} · assistente Rê Leve</p>
          <p className="cs-chat-status">
            Online agora · em Recife{calorieActive ? ' · 📊 acompanhamento ativo' : ''}
          </p>
        </div>
      </div>

      {/* Card de checkout simulado */}
      {checkoutState && (
        <CheckoutCard
          checkout={checkoutState}
          calorieConsent={calorieActive}
          onClear={handleClearCheckout}
          onActivateCalorie={handleActivateCalorie}
        />
      )}

      {/* Painel de pagamento — aparece após kit confirmado */}
      {checkoutState && (
        <PaymentPanelClient
          clienteId={clienteId}
          kit={checkoutState.kitName}
          onPaymentApproved={handlePaymentApproved}
        />
      )}

      <div className="cs-chat-messages">
        {messages.map(m => (
          <div key={m.id}>
            <div className={`cs-bubble cs-bubble--${m.sender}`}>{m.text}</div>

            {/* Badge de fonte (LLM / Determinístico / Fallback) */}
            {m.sender === 'bot' && m.source && m.source !== 'local' && (
              <div style={{ marginBottom: 4 }}>
                <span className={`response-source-badge response-source-badge--${
                  m.source === 'llm' ? 'llm' : m.source === 'fallback' ? 'fallback' : 'deterministic'
                }`}>
                  {m.source === 'llm' ? '🤖 LLM' : m.source === 'fallback' ? '🔵 Fallback' : '⚙️ Determinístico'}
                </span>
              </div>
            )}

            {/* Botão confirmar kit */}
            {m.sender === 'bot' && m.recommendation && !m.confirmedKit && (
              <div className="cs-kit-confirm-row">
                <button className="cs-btn cs-btn--confirm-kit" onClick={() => handleConfirmKit(m.recommendation)}>
                  ✅ Confirmar este kit
                </button>
                <button className="cs-btn cs-btn--ghost-sm" onClick={handleCrossSell}>
                  🥤 Snack ou suco?
                </button>
              </div>
            )}

            {/* Badge kit confirmado */}
            {m.sender === 'bot' && m.confirmedKit && (
              <div className="cs-badge-row">
                <span className="cs-badge cs-badge--confirmed">🛒 Kit confirmado</span>
                {m.caloriePlanStatus?.success && (
                  <span className="cs-badge cs-badge--calorie">📊 Plano calórico criado</span>
                )}
                <span className="cs-badge cs-badge--whatsapp">💬 WhatsApp pronto</span>
              </div>
            )}

            {/* Refeição registrada */}
            {m.sender === 'bot' && m.mealRegistered && (
              <div className="cs-badge-row">
                <span className="cs-badge cs-badge--meal">🍽️ Refeição registrada</span>
              </div>
            )}

            {/* Botões de consentimento */}
            {m.sender === 'bot' && m.shouldAskConsent && pendingConsent && (
              <div className="cs-consent-buttons">
                <button className="cs-consent-btn cs-consent-btn--yes" onClick={handleConsentYes}>
                  ✅ Sim, autorizo
                </button>
                <button className="cs-consent-btn cs-consent-btn--no" onClick={handleConsentNo}>
                  ❌ Agora não
                </button>
              </div>
            )}

            {/* CTA humano */}
            {m.sender === 'bot' && m.shouldEscalate && (
              <div className="cs-escalate-cta">
                <a
                  {...appConfig.whatsAppAnchorProps('Oi! Preciso falar com alguém do atendimento da Rê Leve.')}
                  className="cs-btn cs-btn--escalate"
                >
                  💬 Falar com atendimento
                </a>
              </div>
            )}

            {/* CTA WhatsApp */}
            {m.sender === 'bot' && m.cta === 'Finalizar pelo WhatsApp' && !m.shouldEscalate && (
              <div className="cs-whatsapp-cta">
                <a
                  {...appConfig.whatsAppAnchorProps('Oi! Quero finalizar meu pedido da Rê Leve. 🌱')}
                  className="cs-btn cs-btn--whatsapp-inline"
                >
                  💬 Finalizar pelo WhatsApp
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Ações contextuais */}
      {calorieActive && (
        <div className="cs-contextual-actions">
          <button className="cs-btn cs-btn--meal-register" onClick={handleRegisterMeal}>
            🍽️ Registrar refeição
          </button>
        </div>
      )}

      <div className="cs-chat-quick">
        {QUICK_BUTTONS.map(b => (
          <button key={b.id} className="cs-quick-btn" onClick={() => handleQuick(b)}>
            {b.label}
          </button>
        ))}
      </div>

      <div className="cs-chat-input-row">
        <input
          className="cs-chat-input"
          type="text"
          placeholder="Digite sua mensagem..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSend()}
        />
        <button className="cs-btn cs-btn--send" onClick={handleSend}>Enviar</button>
      </div>
    </div>
  );
}

// ── Kits em destaque ───────────────────────────────────────────────────────────

const KITS_DESTAQUE_IDS = ['KIT_001', 'KIT_004', 'KIT_005', 'KIT_006'];

function KitsDestaque() {
  const kits = getAllKits().filter(k => KITS_DESTAQUE_IDS.includes(k.id));
  return (
    <section className="cs-section">
      <h2 className="cs-section-title">Kits em destaque</h2>
      <div className="cs-kits-grid">
        {kits.map(kit => (
          <div key={kit.id} className="cs-kit-card">
            <p className="cs-kit-icon">🥗</p>
            <h3 className="cs-kit-name">{kit.nome}</h3>
            <p className="cs-kit-uso">{kit.usoRecomendado}</p>
            <ul className="cs-kit-composicao">
              {(kit.composicao || []).slice(0, 4).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            {kit.faixaCalorica && <p className="cs-kit-cal">⚡ {kit.faixaCalorica}</p>}
            <button className="cs-btn cs-btn--kit">
              {kit.ctaSugerido ? kit.ctaSugerido.split('!')[0] + '!' : 'Montar kit'}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Promoções ──────────────────────────────────────────────────────────────────

function Promocoes() {
  const promocoes = getAllPromocoes();
  return (
    <section className="cs-section cs-section--alt">
      <h2 className="cs-section-title">Promoções e programas</h2>
      <div className="cs-promos-grid">
        {promocoes.map(p => (
          <div key={p.id} className={`cs-promo-card ${p.destaque ? 'cs-promo-card--destaque' : ''}`}>
            <span className="cs-promo-icon">{p.icone}</span>
            {p.badge && <span className="cs-promo-badge">{p.badge}</span>}
            <h3 className="cs-promo-titulo">{p.titulo}</h3>
            <p className="cs-promo-desc">{p.descricao}</p>
            <button className="cs-btn cs-btn--promo">{p.cta}</button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Indicação ──────────────────────────────────────────────────────────────────

function Indicacao() {
  return (
    <section className="cs-section">
      <div className="cs-indicacao-card">
        <span className="cs-indicacao-icon">🤝</span>
        <div>
          <h3 className="cs-indicacao-title">Programa de indicação Rê Leve</h3>
          <p className="cs-indicacao-desc">
            Indique alguém e ganhe um benefício na próxima compra. Simples, rápido e sem complicação.
          </p>
          <button className="cs-btn cs-btn--indicar">Quero indicar um amigo</button>
        </div>
      </div>
    </section>
  );
}

// ── Acompanhamento calórico ────────────────────────────────────────────────────

function AcompanhamentoCalorico({ simCustomer }) {
  const clienteId = simCustomer?.clienteId || simCustomer?.id || null;
  const [consentActive, setConsentActive] = useState(() =>
    clienteId ? hasCalorieConsent(clienteId) : false
  );
  return (
    <section className="cs-section cs-section--alt">
      <CalorieTrackerPanel customer={simCustomer} mode="client" onConsentActivate={() => setConsentActive(true)} />
    </section>
  );
}

// ── CTA WhatsApp final ─────────────────────────────────────────────────────────

function CtaWhatsApp() {
  return (
    <section className="cs-section cs-cta-section">
      <h2 className="cs-cta-title">Bora começar leve?</h2>
      <p className="cs-cta-subtitle">Manda ver no WhatsApp — eu monto teu pedido em poucos cliques. Tira o peso de pensar no almoço todo dia.</p>
      <a {...appConfig.whatsAppAnchorProps()} className="cs-btn cs-btn--whatsapp">💬 Finalizar pelo WhatsApp</a>
    </section>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────

function ChatbotTabs({ activeTab, onChange }) {
  const tabs = [
    { id: 'sales', label: '🛒 Comprar com o bot', subtitle: 'Venda & Relacionamento' },
    { id: 'faq',   label: '❓ Tirar dúvidas (FAQ)', subtitle: 'Atendimento automático' },
  ];
  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 12,
      borderBottom: '1px solid #e5e7eb', paddingBottom: 4,
    }}>
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          style={{
            flex: 1, padding: '10px 12px',
            background: activeTab === t.id ? '#065f46' : '#f9fafb',
            color: activeTab === t.id ? '#fff' : '#374151',
            border: 'none', borderRadius: '8px 8px 0 0',
            cursor: 'pointer', fontWeight: 600, fontSize: 13,
            transition: 'all 0.15s',
          }}
        >
          <div>{t.label}</div>
          <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 400, marginTop: 2 }}>
            {t.subtitle}
          </div>
        </button>
      ))}
    </div>
  );
}

export default function ClientSiteView({ simCustomer }) {
  // Garante clienteId mesmo para visitante anônimo — pagamento e checkout funcionam sem login
  const effectiveCustomer = simCustomer || { clienteId: 'GUEST_DEMO', id: 'GUEST_DEMO' };
  const [activeTab, setActiveTab] = useState('sales');

  return (
    <div className="cs-site">
      <Hero />
      <VideoBrand />
      <div className="cs-main-grid">
        <div className="cs-chat-column">
          <h2 className="cs-column-title">
            {activeTab === 'sales' ? 'Assistente Rê Leve' : 'FAQ Rê Leve'}
          </h2>
          <ChatbotTabs activeTab={activeTab} onChange={setActiveTab} />
          {activeTab === 'sales' ? (
            <ClientChat simCustomer={effectiveCustomer} />
          ) : (
            <FAQChatbotPanel clienteId={effectiveCustomer.clienteId} />
          )}
        </div>
        <div className="cs-content-column">
          <KitsDestaque />
        </div>
      </div>
      <Promocoes />
      <Indicacao />
      <AcompanhamentoCalorico simCustomer={effectiveCustomer} />
      <CtaWhatsApp />

      {/* Indicador discreto de modo — visível apenas no rodapé */}
      <footer style={{
        textAlign: 'center', padding: '14px 0', fontSize: 11,
        color: 'var(--rl-ink-2, #5A5F52)', borderTop: '1px solid var(--rl-line, #DDD6C5)', marginTop: 8,
      }}>
        🌱 Rê Leve · Comida leve, prática e saudável para o dia a dia
        {appConfig.isDemoMode && (
          <span style={{ marginLeft: 8, color: 'var(--rl-mostard-deep, #C97C13)' }}>· Modo demonstração</span>
        )}
      </footer>

      {/* Bottom nav nativo do PWA (mobile ≤768px) */}
      <MobileBottomNav />
    </div>
  );
}
